// The conversation: messages under a separator per day (Today, Yesterday, then the date in your format), each a
// bubble with its formatted text, its photos (one as it is, several as a grid) and its files (cards), the
// reactions underneath and "edited" by its time. Hovering a message offers the quick reactions, and Edit and
// Delete on your own; on a touch screen a long press opens the same as a sheet, with Copy text.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { CHAT_REACTIONS, chatDisplayHtml, chatFileKind, formatFileSize, type ChatAttachment, type ChatReactionEmoji } from '@technohull/shared';
import { useDateFormatter } from '../lib/format.js';
import { useBackToClose, useIsCoarse } from '../lib/media.js';
import { IconCopy, IconDownload, IconPencil, IconSmile, IconTrash } from '../components/icons.js';
import { chatFileUrl, type CollabMessage } from './collabHooks.js';
import { chatDayKey, chatDayLabel, fileBadge, photoAspect } from './chatFormat.js';

export interface MessageActions {
  onReact: (m: CollabMessage, emoji: ChatReactionEmoji, on: boolean) => void;
  onEdit: (m: CollabMessage) => void;
  onDelete: (m: CollabMessage) => void;
  onOpenPhoto: (id: string) => void;
  /** a photo finished loading (the list may need to stay scrolled to the bottom) */
  onMediaLoad: () => void;
  /** the focus back to the message box, when the button that had it is going away */
  focusComposer: () => void;
}

export function ChatMessages({ messages, editingId, ...actions }: { messages: CollabMessage[]; editingId: number | null } & MessageActions) {
  const df = useDateFormatter();
  const [picker, setPicker] = useState<number | null>(null);
  const [sheet, setSheet] = useState<CollabMessage | null>(null);

  // the reaction picker closes on Escape or a press anywhere else
  useEffect(() => {
    if (picker == null) return;
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPicker(null); } };
    const down = (e: PointerEvent) => { if (!(e.target as Element)?.closest?.('.chat-react-pick, .chat-act-react')) setPicker(null); };
    window.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', down, true);
    return () => { window.removeEventListener('keydown', key, true); document.removeEventListener('pointerdown', down, true); };
  }, [picker]);

  const rows: ReactNode[] = [];
  let day = '';
  messages.forEach((m, i) => {
    const d = chatDayKey(m.at);
    if (d !== day) {
      day = d;
      rows.push(<div key={`day-${d}`} className="chat-day" role="separator"><span>{chatDayLabel(m.at, df)}</span></div>);
    }
    const prev = messages[i - 1];
    const showName = !m.mine && (!prev || prev.fromUid !== m.fromUid || chatDayKey(prev.at) !== d);
    rows.push(
      <Message
        key={m.id}
        m={m}
        showName={showName}
        editing={editingId === m.id}
        time={df.time(m.at)}
        edited={m.editedAt ? `Edited ${df.dateTime(m.editedAt, { year: 'auto' })}` : null}
        picking={picker === m.id}
        onPick={(open) => setPicker(open ? m.id : null)}
        onLongPress={() => setSheet(m)}
        {...actions}
      />,
    );
  });
  return (
    <>
      {rows}
      {sheet && <ActionSheet m={sheet} onClose={() => setSheet(null)} {...actions} />}
    </>
  );
}

function Message({ m, showName, editing, time, edited, picking, onPick, onLongPress, onReact, onEdit, onDelete, onOpenPhoto, onMediaLoad, focusComposer }: {
  m: CollabMessage;
  showName: boolean;
  editing: boolean;
  time: string;
  edited: string | null;
  picking: boolean;
  onPick: (open: boolean) => void;
  onLongPress: () => void;
} & MessageActions) {
  const coarse = useIsCoarse();
  const html = useMemo(() => (m.deleted ? '' : chatDisplayHtml(m.html, m.body)), [m.deleted, m.html, m.body]);
  const photos = m.attachments.filter((a) => chatFileKind(a.type) === 'image');
  const files = m.attachments.filter((a) => chatFileKind(a.type) !== 'image');
  const press = useLongPress(onLongPress);
  // the hover bar sits beside the bubble, on the free side; my own bar (three buttons) goes above a bubble
  // too wide to leave it room there
  const [actsAbove, setActsAbove] = useState(false);
  const placeActs = (e: { currentTarget: HTMLElement }) => {
    const line = e.currentTarget;
    const box = line.closest<HTMLElement>('.chat-msgs');
    const acts = line.querySelector<HTMLElement>('.chat-acts');
    if (!m.mine || !box || !acts) return;
    const free = line.getBoundingClientRect().left - box.getBoundingClientRect().left - box.clientLeft;
    setActsAbove(free < acts.offsetWidth + 10);
  };

  return (
    <div className={`chat-msg${m.mine ? ' mine' : ''}${m.deleted ? ' is-deleted' : ''}${editing ? ' is-editing' : ''}`} data-msg-id={m.id}>
      {showName && <span className="chat-msg-name">{m.fromName}</span>}
      <div className={`chat-msg-line${actsAbove ? ' acts-above' : ''}`} onPointerEnter={coarse ? undefined : placeActs} onFocus={coarse ? undefined : placeActs}>
        <div className={`chat-bubble${!html && photos.length ? ' only-media' : ''}`} {...(coarse && !m.deleted ? press : {})}>
          {m.deleted ? (
            <span className="chat-deleted">Message deleted</span>
          ) : (
            <>
              {html && <div className="chat-rich" dangerouslySetInnerHTML={{ __html: html }} />}
              {photos.length > 0 && <Photos photos={photos} onOpen={onOpenPhoto} onLoad={onMediaLoad} />}
              {files.map((f) => <FileCard key={f.id} f={f} />)}
            </>
          )}
        </div>
        {!m.deleted && !coarse && (
          <div className={`chat-acts${picking ? ' is-open' : ''}`}>
            <button type="button" className="chat-act chat-act-react" aria-label="React" title="React" aria-expanded={picking} onClick={() => onPick(!picking)}>
              <IconSmile size={15} />
            </button>
            {/* right after its button, so Tab goes on into it */}
            {picking && (
              <ReactPick
                m={m}
                onChoose={(e, on) => {
                  onReact(m, e, on);
                  onPick(false);
                  focusComposer(); // the picker (and the button pressed in it) goes
                }}
              />
            )}
            {m.mine && (
              <>
                <button type="button" className="chat-act" aria-label="Edit message" title="Edit" onClick={() => onEdit(m)}><IconPencil size={14} /></button>
                <button type="button" className="chat-act" aria-label="Delete message" title="Delete" onClick={() => onDelete(m)}><IconTrash size={14} /></button>
              </>
            )}
          </div>
        )}
      </div>
      {m.reactions.length > 0 && (
        <div className="chat-reacts">
          {m.reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              className={`chat-react${r.mine ? ' on' : ''}`}
              title={r.names.join(', ')}
              aria-label={`${r.emoji} ${r.count}: ${r.names.join(', ')}`}
              aria-pressed={r.mine}
              onClick={() => {
                onReact(m, r.emoji as ChatReactionEmoji, !r.mine);
                if (r.mine && r.count === 1) focusComposer(); // the chip goes with my reaction
              }}
            >
              <span className="chat-react-e">{r.emoji}</span>
              <span className="chat-react-n">{r.count}</span>
            </button>
          ))}
        </div>
      )}
      <span className="chat-msg-time">
        {time}
        {edited && <span className="chat-edited" title={edited}> · edited</span>}
      </span>
    </div>
  );
}

/** the quick reactions over a message's action bar. They stay inside the conversation: shifted in from its
 *  side when the bubble is narrow, and put under the message when there's no room above it (the first
 *  messages of a thread). */
function ReactPick({ m, onChoose }: { m: CollabMessage; onChoose: (emoji: ChatReactionEmoji, on: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const bar = el?.offsetParent as HTMLElement | null | undefined; // the action bar it hangs from
    const box = el?.closest<HTMLElement>('.chat-msgs');
    const line = el?.closest<HTMLElement>('.chat-msg-line');
    if (!el || !bar || !box || !line) return;
    const b = box.getBoundingClientRect();
    const gap = 6;
    const left = b.left + box.clientLeft + gap;
    const right = b.left + box.clientLeft + box.clientWidth - gap;
    const top = b.top + box.clientTop + gap;
    const bottom = b.top + box.clientTop + box.clientHeight - gap;
    let r = el.getBoundingClientRect();
    if (r.top < top) {
      const under = line.getBoundingClientRect().bottom + 4;
      const at = under + r.height <= bottom ? under : top; // no room under it either: at the top, over it
      el.style.bottom = 'auto';
      el.style.top = `${Math.round(at - bar.getBoundingClientRect().top - bar.clientTop)}px`;
      r = el.getBoundingClientRect();
    }
    const dx = r.left < left ? left - r.left : r.right > right ? right - r.right : 0;
    if (dx) el.style.transform = `translateX(${Math.round(dx)}px)`;
  }, []);
  return (
    <div ref={ref} className="chat-react-pick" role="group" aria-label="Reactions">
      {CHAT_REACTIONS.map((e) => {
        const mine = !!m.reactions.find((r) => r.emoji === e)?.mine;
        return (
          <button key={e} type="button" className={mine ? 'on' : ''} aria-label={`React ${e}`} aria-pressed={mine} onClick={() => onChoose(e, !mine)}>
            {e}
          </button>
        );
      })}
    </div>
  );
}

/** one photo at its own shape, or several as a grid (four at most, "+n" on the last). */
function Photos({ photos, onOpen, onLoad }: { photos: ChatAttachment[]; onOpen: (id: string) => void; onLoad: () => void }) {
  if (photos.length === 1) {
    const p = photos[0]!;
    return (
      <button
        type="button"
        className="chat-photo one"
        // a small image keeps its own size rather than being blown up
        style={{ aspectRatio: String(photoAspect(p)), ...(p.width && p.width < 240 ? { width: Math.max(96, p.width) } : {}) }}
        onClick={() => onOpen(p.id)}
        aria-label={`Open photo ${p.name}`}
      >
        <img src={chatFileUrl(p.id)} alt={p.name} loading="lazy" decoding="async" onLoad={onLoad} />
      </button>
    );
  }
  const shown = photos.slice(0, 4);
  const more = photos.length - shown.length;
  return (
    <div className={`chat-photos n${shown.length}`}>
      {shown.map((p, i) => (
        <button key={p.id} type="button" className="chat-photo" onClick={() => onOpen(p.id)} aria-label={`Open photo ${p.name}`}>
          <img src={chatFileUrl(p.id)} alt={p.name} loading="lazy" decoding="async" onLoad={onLoad} />
          {i === shown.length - 1 && more > 0 && <span className="chat-photo-more">+{more}</span>}
        </button>
      ))}
    </div>
  );
}

/** a file: its type, name and size. A PDF opens in a new tab (with its own download button); anything
 *  else downloads. */
function FileCard({ f }: { f: ChatAttachment }) {
  const badge = fileBadge(f);
  const pdf = chatFileKind(f.type) === 'pdf';
  return (
    <div className="chat-file">
      <a
        className="chat-file-main"
        href={chatFileUrl(f.id, !pdf)}
        {...(pdf ? { target: '_blank', rel: 'noopener noreferrer' } : { download: f.name })}
        title={pdf ? `Open ${f.name}` : `Download ${f.name}`}
      >
        <span className={`chat-file-ic t-${badge.tone}`} aria-hidden="true">{badge.label}</span>
        <span className="chat-file-txt">
          <span className="chat-file-name">{f.name}</span>
          <span className="chat-file-meta">{formatFileSize(f.size)}{pdf ? ' · PDF' : ''}</span>
        </span>
      </a>
      <a className="chat-file-dl" href={chatFileUrl(f.id, true)} download={f.name} aria-label={`Download ${f.name}`} title="Download">
        <IconDownload size={16} />
      </a>
    </div>
  );
}

/** touch: the long-press menu for a message: the quick reactions, Copy text, and Edit / Delete on your own. */
function ActionSheet({ m, onClose, onReact, onEdit, onDelete }: { m: CollabMessage; onClose: () => void } & MessageActions) {
  useBackToClose(true, onClose);
  const box = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // once, on open (the thread re-renders on every poll)
  useEffect(() => {
    box.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeRef.current(); } };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, []);
  const act = (fn: () => void) => () => { onClose(); fn(); };
  // the finger that opened the sheet lifts over it: that click is not a choice, so only a press that
  // starts on the sheet counts (a keyboard click has no press: detail 0)
  const armed = useRef(false);
  return (
    <div
      className="chat-sheet-bg"
      onPointerDownCapture={() => { armed.current = true; }}
      onClickCapture={(e) => { if (!armed.current && e.detail !== 0) { e.preventDefault(); e.stopPropagation(); } }}
      onClick={onClose}
    >
      <div ref={box} tabIndex={-1} className="chat-sheet" role="dialog" aria-label="Message" onClick={(e) => e.stopPropagation()}>
        <div className="chat-sheet-reacts" role="group" aria-label="Reactions">
          {CHAT_REACTIONS.map((e) => {
            const mine = !!m.reactions.find((r) => r.emoji === e)?.mine;
            return (
              <button key={e} type="button" className={mine ? 'on' : ''} aria-label={`React ${e}`} aria-pressed={mine} onClick={act(() => onReact(m, e, !mine))}>{e}</button>
            );
          })}
        </div>
        {m.body && (
          <button type="button" className="chat-sheet-row" onClick={act(() => void navigator.clipboard?.writeText(m.body).catch(() => undefined))}>
            <IconCopy size={17} /> Copy text
          </button>
        )}
        {m.mine && (
          <>
            <button type="button" className="chat-sheet-row" onClick={act(() => onEdit(m))}><IconPencil size={17} /> Edit</button>
            <button type="button" className="chat-sheet-row danger" onClick={act(() => onDelete(m))}><IconTrash size={17} /> Delete</button>
          </>
        )}
        <button type="button" className="chat-sheet-row chat-sheet-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/** a long press with a finger (and not a scroll): calls `fn`, and swallows the tap that would follow it. */
function useLongPress(fn: () => void) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const cancel = () => { if (timer.current != null) window.clearTimeout(timer.current); timer.current = null; };
  useEffect(() => cancel, []);
  return {
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.pointerType !== 'touch') return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      cancel();
      timer.current = window.setTimeout(() => { timer.current = null; fired.current = true; navigator.vibrate?.(10); fn(); }, 450);
    },
    onPointerMove: (e: ReactPointerEvent) => {
      if (timer.current != null && start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onContextMenu: (e: ReactMouseEvent) => e.preventDefault(),
    onClickCapture: (e: ReactMouseEvent) => {
      if (fired.current) { e.preventDefault(); e.stopPropagation(); fired.current = false; }
    },
  };
}
